// BentoTile — Generic Wrapper fuer Bento-Grid-Children.
//
// Container-only. Hat KEINE Daten-Logik — wrappt eine konsumierende Tile-
// Komponente und liefert: gridArea-Placement, Severity/Firma-Border-Style,
// klickbarer Hover-Lift, optional onClick fuer Drilldown-Drawer (Push 2).
//
// Spec-Rule: niemals Severity UND Firmen-Color gleichzeitig setzen.
// Component erzwingt das per Pflicht-XOR (TS-Union).
//
// Sizes existieren nur als Doku-Tag — die tatsaechliche Groesse kommt aus
// dem grid-template-areas in BentoGrid. `size` ist Metadata + a11y-Label.

import type { CSSProperties, ReactNode } from "react";

import type { BentoArea } from "./BentoGrid";
import type { FirmaSlug } from "../../lib/firma-colors";
import { FIRMA_COLORS } from "../../lib/firma-colors";
import type { SeverityInput } from "../../lib/severity";
import { normalizeSeverity, severityToTailwind } from "../../lib/severity";

export type BentoSize = "1x1" | "1x2" | "2x1" | "2x2" | "3x1" | "4x2" | "7x2";

interface BaseProps {
  area: BentoArea;
  size: BentoSize;
  children: ReactNode;
  /** Klickbar → Hover-Lift + Cursor-Pointer + optional Drilldown-Open. */
  onClick?: () => void;
  /** ARIA-Label fuer Sections ohne sichtbaren Header (T21-Polish-Pattern). */
  ariaLabel?: string;
  /** Optionaler Extra-Klassen-Slot (z.B. fuer Custom-Padding). */
  className?: string;
  /** Stagger-Index fuer Initial-Load-Animation (kommt automatisch von
   *  BentoGrid.withStagger). 50ms Delay pro Tile (Sprint 6 Push 3). */
  staggerIndex?: number;
}

interface SeverityProps extends BaseProps {
  severity: SeverityInput;
  firma?: never;
}

interface FirmaProps extends BaseProps {
  firma: FirmaSlug;
  severity?: never;
}

interface NeutralProps extends BaseProps {
  severity?: never;
  firma?: never;
}

export type BentoTileProps = SeverityProps | FirmaProps | NeutralProps;

const BASE_CLASSES =
  "relative rounded-2xl border p-4 sm:p-6 overflow-hidden " +
  "bg-gradient-to-br transition-all duration-200 bento-fade-in-up";

// Sprint 6 Push 3: Spec-Update auf translate-y-1 + shadow-xl (vorher
// -translate-y-0.5 + shadow-lg). Greift nur auf Desktop-Hover (md+),
// auf Mobile/Touch ist active:scale subtiler.
const HOVER_CLASSES =
  "md:hover:-translate-y-1 md:hover:shadow-xl active:scale-[0.99] cursor-pointer focus-visible:ring-2 focus-visible:ring-sky-500/40 focus-visible:outline-none";

export default function BentoTile(props: BentoTileProps) {
  const { area, size, children, onClick, ariaLabel, className, staggerIndex } = props;

  // Style-Resolution: Severity gewinnt (Spec-Konvention), sonst Firma, sonst
  // Neutral. TS-Union verhindert dass beide gleichzeitig gesetzt sind.
  let toneClasses: string;
  let glowClass = "";
  let pulseClass = "";
  // Design-Wurf 03.07.: Firmen-Tiles bekommen einen linken Vollfarb-Balken
  // (Markenfarbe flaechig, nicht nur duenner Rand). Severity bleibt ohne
  // Balken — Glow+Pulse sind deren Kennung, so bleiben beide Systeme
  // auf einen Blick unterscheidbar.
  let accentBar = "";
  if ("severity" in props && props.severity !== undefined) {
    const sev = normalizeSeverity(props.severity);
    const style = severityToTailwind(props.severity);
    toneClasses = `${style.border} ${style.bg} from-transparent to-transparent`;
    if (sev === "critical") {
      glowClass = style.glow;
      // Sprint 6 Push 3: subtiler Critical-Pulse (1.8s, 70%-100% opacity).
      pulseClass = "bento-pulse-slow";
    }
  } else if ("firma" in props && props.firma) {
    const palette = FIRMA_COLORS[props.firma];
    toneClasses = `${palette.border} ${palette.gradient}`;
    accentBar = palette.accentBar;
  } else {
    toneClasses = "border-slate-700/50 bg-slate-900/40 from-transparent to-transparent";
  }

  const interactive = typeof onClick === "function";
  // Stagger-Delay (50ms pro Tile); cap bei 600ms damit spaete Tiles nicht
  // sichtbar nachhinken bei vielen Children.
  const delayMs = Math.min((staggerIndex ?? 0) * 50, 600);
  const style: CSSProperties = {
    gridArea: area,
    animationDelay: delayMs > 0 ? `${delayMs}ms` : undefined,
  };

  const combined = [
    BASE_CLASSES,
    toneClasses,
    glowClass,
    pulseClass,
    interactive ? HOVER_CLASSES : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const sharedProps = {
    style,
    "data-bento-size": size,
    "data-bento-area": area,
    "aria-label": ariaLabel,
    className: combined,
  };

  // Linker Markenfarb-Balken (Design-Wurf 03.07.) — liegt im overflow-hidden
  // des Containers, rundet sich also automatisch mit.
  const bar = accentBar ? (
    <span aria-hidden className={`absolute inset-y-0 left-0 w-1.5 ${accentBar}`} />
  ) : null;

  if (interactive) {
    return (
      <button type="button" onClick={onClick} {...sharedProps}>
        {bar}
        {children}
      </button>
    );
  }
  return (
    <section role="region" {...sharedProps}>
      {bar}
      {children}
    </section>
  );
}
